import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SCHOOLS, SKILL_MAP, validateProfile } from '@crossmatch/shared';
import type { AbilityDimension, SchoolId } from '@crossmatch/shared';
import { meApi } from '../api/me.js';
import type { ProfileDto, ProfilePayload } from '../api/types.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { SkillPicker } from '../components/SkillPicker.js';
import { Tag } from '../components/Tag.js';
import { formatHours, schoolFullLabel, skillLabel } from '../lib/format.js';
import { ABILITY_LABELS, ABILITY_ORDER, CATEGORY_TO_ABILITY } from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';
import { SLOT_OPTIONS } from '../lib/slots.js';
import { useMeQuery } from '../hooks/queries.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

type FieldErrorKey = 'school' | 'skills' | 'weeklyHours';

interface ProfileFormState {
  school: SchoolId;
  major: string;
  grade: string;
  skills: string[];
  weeklyHoursText: string;
  slots: string[];
  strengths: AbilityDimension[];
  weaknesses: AbilityDimension[];
  headline: string;
}

const DEFAULT_HOURS = '10';

function toFormState(profile: ProfileDto | null, fallbackSchool: SchoolId): ProfileFormState {
  if (!profile) {
    return {
      school: fallbackSchool,
      major: '',
      grade: '',
      skills: [],
      weeklyHoursText: DEFAULT_HOURS,
      slots: [],
      strengths: [],
      weaknesses: [],
      headline: '',
    };
  }
  return {
    school: profile.school,
    major: profile.major ?? '',
    grade: profile.grade ?? '',
    skills: [...profile.skills],
    weeklyHoursText: String(profile.weeklyHours),
    slots: [...(profile.slots ?? [])],
    strengths: [...(profile.strengths ?? [])],
    weaknesses: [...(profile.weaknesses ?? [])],
    headline: profile.headline ?? '',
  };
}

interface ProfileFormProps {
  initial: ProfileDto | null;
  fallbackSchool: SchoolId;
  onSaved: () => void;
}

function ProfileForm({ initial, fallbackSchool, onSaved }: ProfileFormProps) {
  const [form, setForm] = useState<ProfileFormState>(() => toFormState(initial, fallbackSchool));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldErrorKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [customSlot, setCustomSlot] = useState('');
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (payload: ProfilePayload) => meApi.updateProfile(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.me() });
      setFormError(null);
      toast.success('画像已保存', '匹配排序与团队互补都会用这份画像');
      onSaved();
    },
    onError: (error) => {
      setFormError(`保存画像失败：${toUserMessage(error)}`);
    },
  });

  const patch = (next: Partial<ProfileFormState>): void => setForm((prev) => ({ ...prev, ...next }));

  const toggleSlot = (slot: string): void => {
    patch({ slots: form.slots.includes(slot) ? form.slots.filter((item) => item !== slot) : [...form.slots, slot] });
  };

  const toggleAbility = (key: 'strengths' | 'weaknesses', dim: AbilityDimension): void => {
    const current = form[key];
    patch({ [key]: current.includes(dim) ? current.filter((item) => item !== dim) : [...current, dim] } as Partial<ProfileFormState>);
  };

  /** 根据已选技能的**大类**预填长板（词表映射，不是匹配算法），之后用户可随意手改 */
  const suggestStrengths = (): void => {
    const suggested = new Set<AbilityDimension>(form.strengths);
    for (const skillId of form.skills) {
      const category = SKILL_MAP[skillId]?.category;
      if (!category) continue;
      suggested.add(CATEGORY_TO_ABILITY[category]);
    }
    patch({ strengths: [...suggested] });
    toast.info('已根据技能预填长板', '可以继续手动增删这些维度');
  };

  const customSlots = useMemo(
    () => form.slots.filter((slot) => !(SLOT_OPTIONS as readonly string[]).includes(slot)),
    [form.slots],
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const hoursText = form.weeklyHoursText.trim();
    const hours = hoursText.length === 0 ? Number.NaN : Number(hoursText);
    const parsed = validateProfile({
      school: form.school,
      skills: form.skills,
      weeklyHours: hours,
      major: form.major,
      grade: form.grade,
      slots: form.slots,
      strengths: form.strengths,
      weaknesses: form.weaknesses,
      headline: form.headline,
    });
    if (!parsed.ok) {
      const next: Partial<Record<FieldErrorKey, string>> = {};
      for (const issue of parsed.issues) {
        if (issue.field === 'school' || issue.field === 'skills' || issue.field === 'weeklyHours') {
          next[issue.field] = issue.message;
        }
      }
      setFieldErrors(next);
      setFormError(`保存画像失败：${parsed.issues.map((issue) => issue.message).join('；')}`);
      return;
    }
    setFieldErrors({});
    setFormError(null);

    const payload: ProfilePayload = {
      school: form.school,
      skills: form.skills,
      weeklyHours: hours,
      slots: form.slots,
      strengths: form.strengths,
      weaknesses: form.weaknesses,
    };
    const major = form.major.trim();
    const grade = form.grade.trim();
    const headline = form.headline.trim();
    if (major.length > 0) payload.major = major;
    if (grade.length > 0) payload.grade = grade;
    if (headline.length > 0) payload.headline = headline;
    save.mutate(payload);
  };

  return (
    <form className="stack" onSubmit={handleSubmit} noValidate>
      {formError !== null ? (
        <div className="banner banner-danger" role="alert">
          <span className="banner-ico" aria-hidden="true">
            !
          </span>
          <span className="wrap-anywhere">{formError}</span>
        </div>
      ) : null}

      <Card title="基本信息" subtitle="学校用于跨校判定；专业/年级选填。">
        <div className="grid-2">
          <div className="field">
            <label className="field-label" htmlFor="profile-school">
              学校（必填）
            </label>
            <select
              id="profile-school"
              className={fieldErrors.school !== undefined ? 'select input-invalid' : 'select'}
              value={form.school}
              onChange={(event) => patch({ school: event.target.value as SchoolId })}
            >
              {SCHOOLS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {fieldErrors.school !== undefined ? <span className="field-error">{fieldErrors.school}</span> : null}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="profile-major">
              专业（选填）
            </label>
            <input
              id="profile-major"
              className="input"
              value={form.major}
              maxLength={40}
              placeholder="如：计算机科学与技术"
              onChange={(event) => patch({ major: event.target.value })}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="profile-grade">
              年级（选填）
            </label>
            <input
              id="profile-grade"
              className="input"
              value={form.grade}
              maxLength={20}
              placeholder="如：大三 / 研一"
              onChange={(event) => patch({ grade: event.target.value })}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="profile-headline">
              一句话自我介绍（选填，≤200 字）
            </label>
            <input
              id="profile-headline"
              className="input"
              value={form.headline}
              maxLength={200}
              placeholder="如：做过 2 个小程序，想找设计同学一起打磨作品"
              onChange={(event) => patch({ headline: event.target.value })}
            />
            <span className="field-hint">已输入 {form.headline.length}/200 字</span>
          </div>
        </div>
      </Card>

      <Card
        title="技能（必填，至少 1 个，最多 20 个）"
        subtitle="只能从词表里选：未收录的词不会计入技能，也不会参与匹配计分。"
      >
        <SkillPicker
          selected={form.skills}
          onChange={(next) => patch({ skills: next })}
          hint="搜索支持中文名/英文名/别名（ps、figma、前端…）。"
        />
        {fieldErrors.skills !== undefined ? <span className="field-error">{fieldErrors.skills}</span> : null}
      </Card>

      <Card title="可投入时间（必填）" subtitle="每周小时数与空闲时段参与「时间协同」匹配。">
        <div className="grid-2">
          <div className="field">
            <label className="field-label" htmlFor="profile-hours">
              每周可投入小时数（0–60）
            </label>
            <input
              id="profile-hours"
              className={fieldErrors.weeklyHours !== undefined ? 'input input-invalid' : 'input'}
              type="number"
              min={0}
              max={60}
              step={1}
              value={form.weeklyHoursText}
              onChange={(event) => patch({ weeklyHoursText: event.target.value })}
            />
            <input
              className="range"
              type="range"
              min={0}
              max={60}
              step={1}
              aria-label="每周可投入小时数滑块"
              value={Number.isFinite(Number(form.weeklyHoursText)) ? Math.max(0, Math.min(60, Number(form.weeklyHoursText))) : 0}
              onChange={(event) => patch({ weeklyHoursText: event.target.value })}
            />
            {fieldErrors.weeklyHours !== undefined ? (
              <span className="field-error">{fieldErrors.weeklyHours}</span>
            ) : (
              <span className="field-hint">当前：{formatHours(Number(form.weeklyHoursText))}</span>
            )}
          </div>

          <div className="field">
            <span className="field-label">可协作时段（多选，可自定义）</span>
            <div className="chip-grid">
              {SLOT_OPTIONS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className={form.slots.includes(slot) ? 'chip on' : 'chip'}
                  aria-pressed={form.slots.includes(slot)}
                  onClick={() => toggleSlot(slot)}
                >
                  {slot}
                </button>
              ))}
            </div>
            {customSlots.length > 0 ? (
              <div className="selected-chips">
                {customSlots.map((slot) => (
                  <button key={slot} type="button" className="chip on" onClick={() => toggleSlot(slot)} title="点击移除">
                    {slot} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            ) : null}
            <div className="inline-form">
              <div className="field">
                <label className="field-label" htmlFor="profile-custom-slot">
                  添加自定义时段
                </label>
                <input
                  id="profile-custom-slot"
                  className="input"
                  value={customSlot}
                  maxLength={12}
                  placeholder="如：周三下午"
                  onChange={(event) => setCustomSlot(event.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const slot = customSlot.trim();
                  if (slot.length === 0) return;
                  if (!form.slots.includes(slot)) patch({ slots: [...form.slots, slot] });
                  setCustomSlot('');
                }}
              >
                添加时段
              </Button>
            </div>
            <span className="field-hint">已选 {form.slots.length} 个时段。</span>
          </div>
        </div>
      </Card>

      <Card title="长板 / 短板（可手改）" subtitle="用同一套能力维度描述；短板会作为可解释理由的一部分展示给队友。">
        <div className="grid-2">
          <div className="field">
            <div className="row">
              <span className="field-label">长板</span>
              <Button variant="ghost" size="sm" onClick={suggestStrengths}>
                根据技能预填
              </Button>
            </div>
            <div className="chip-grid">
              {ABILITY_ORDER.map((dim) => (
                <button
                  key={dim}
                  type="button"
                  className={form.strengths.includes(dim) ? 'chip on' : 'chip'}
                  aria-pressed={form.strengths.includes(dim)}
                  onClick={() => toggleAbility('strengths', dim)}
                >
                  {ABILITY_LABELS[dim]}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="field-label">短板</span>
            <div className="chip-grid">
              {ABILITY_ORDER.map((dim) => (
                <button
                  key={dim}
                  type="button"
                  className={form.weaknesses.includes(dim) ? 'chip on' : 'chip'}
                  aria-pressed={form.weaknesses.includes(dim)}
                  onClick={() => toggleAbility('weaknesses', dim)}
                >
                  {ABILITY_LABELS[dim]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div className="row">
        <Button type="submit" variant="primary" loading={save.isPending}>
          {save.isPending ? '正在保存…' : '保存画像'}
        </Button>
      </div>
    </form>
  );
}

/** 画像页：loading / empty（无画像引导填写）/ error 三态齐全。 */
export function ProfilePage() {
  const meQuery = useMeQuery();
  const user = useSession((state) => state.user);
  const fallbackSchool: SchoolId = user?.school ?? 'other';
  const [started, setStarted] = useState(false);

  if (meQuery.isPending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">我的画像</h1>
        </div>
        <Loading label="正在读取你的画像…" rows={4} />
      </div>
    );
  }

  if (meQuery.isError) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">我的画像</h1>
        </div>
        <ErrorState error={meQuery.error} title="读取画像失败" onRetry={() => void meQuery.refetch()} />
      </div>
    );
  }

  const profile = meQuery.data.profile;
  const profileKey = profile
    ? `${profile.school}|${profile.skills.join(',')}|${profile.weeklyHours}|${profile.headline ?? ''}|${(
        profile.slots ?? []
      ).join(',')}`
    : 'empty';

  const myPublicId = meQuery.data.user.publicId ?? user?.publicId ?? null;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">我的画像</h1>
          <p className="page-sub">
            {profile
              ? `当前画像：${schoolFullLabel(profile.school)} · ${profile.skills.length} 个技能 · ${formatHours(
                  profile.weeklyHours,
                )}`
              : '学校、技能、每周可投入时间三项必填。'}
          </p>
        </div>
        {profile && profile.skills.length > 0 ? (
          <div className="row-tight">
            {profile.skills.slice(0, 6).map((skillId) => (
              <Tag key={skillId} tone="primary">
                {skillLabel(skillId)}
              </Tag>
            ))}
            {profile.skills.length > 6 ? <Tag tone="muted">+{profile.skills.length - 6}</Tag> : null}
          </div>
        ) : null}
      </div>

      {myPublicId !== null ? (
        <Card title="我的邀请 ID" subtitle="把这个 ID 发给队长，TA 就能在队伍页「按 ID 查找」找到并邀请你。">
          <div className="row-tight">
            <Tag tone="primary">{myPublicId}</Tag>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(myPublicId);
                toast.success('已复制邀请 ID', myPublicId);
              }}
            >
              复制
            </Button>
          </div>
          <p className="field-hint">该 ID 在注册时自动生成、唯一且不可修改；请不要把密码或验证码透露给任何人。</p>
        </Card>
      ) : null}

      {user?.consentAcceptedAt !== undefined && user?.consentAcceptedAt !== null ? (
        <Card title="协议同意留痕" subtitle="合规要求：你勾选并同意《用户协议》与《隐私政策》的时间。">
          <p className="field-hint">
            已于{' '}
            {new Date(user.consentAcceptedAt).toLocaleString('zh-CN', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}{' '}
            同意《用户协议》与《隐私政策》。
          </p>
        </Card>
      ) : (
        <Card title="协议同意留痕" subtitle="合规要求">
          <p className="field-hint">尚未记录协议同意信息（历史账号或注册时未勾选）。</p>
        </Card>
      )}

      {profile === null && !started ? (
        <EmptyState
          icon="✦"
          title="还没有画像，匹配引擎不知道你能做什么"
          description="填学校 + 至少 1 个技能 + 每周可投入时间即可完成画像。填完之后，候选人池与匹配排序才有依据。"
          action={
            <Button variant="primary" onClick={() => setStarted(true)}>
              开始填写画像
            </Button>
          }
        />
      ) : (
        <ProfileForm key={profileKey} initial={profile} fallbackSchool={fallbackSchool} onSaved={() => setStarted(true)} />
      )}
    </div>
  );
}